import type { RefObject } from 'react'

import { useEffect, useRef, useState } from 'react'

/** Tracks whether an element is on screen, so animations only run while someone can see them. */
export function useInView<T extends Element>(rootMargin = '0px', once = false): { ref: RefObject<T | null>, inView: boolean } {
    const ref = useRef<T | null>(null)
    const [inView, setInView] = useState(false)

    useEffect(() => {
        const node = ref.current
        if (!node) return

        if (typeof IntersectionObserver === 'undefined') {
            setInView(true)
            return
        }

        const observer = new IntersectionObserver(([entry]) => {
            if (!entry) return

            if (entry.isIntersecting) {
                setInView(true)
                if (once) observer.disconnect()
            }
            else if (!once) {
                setInView(false)
            }
        }, { rootMargin })

        observer.observe(node)

        return () => observer.disconnect()
    }, [rootMargin, once])

    return { ref, inView }
}
